/**
 * HEAREN BUILD 56 - Catalog Service
 * 
 * Centralized service for fetching Radio and Soundscapes catalog data.
 * All catalog requests go through this service to ensure:
 * - Cache usage with proper TTL
 * - Request deduplication
 * - Performance logging
 * - Lang normalization
 * 
 * IMPORTANT: Pages should NEVER fetch catalog data directly.
 * Always use this service's functions.
 */

import { 
  cachedFetch, 
  preload, 
  CACHE_KEYS, 
  CACHE_TTL, 
  normalizeLang,
  getLastSelectedTheme,
  saveLastSelectedTheme,
  getFromCache
} from './cacheService';
import { API_BASE_URL } from './httpClient';

// Performance timing utility
const perfTimer = () => {
  const start = performance.now();
  return {
    elapsed: () => Math.round(performance.now() - start)
  };
};

// =============================================================================
// RADIO CATALOG
// =============================================================================

/**
 * Get radio themes (categories)
 * @param {string} lang - Language code (fr/en)
 * @returns {Promise<Array>} - Array of theme objects
 */
export const getRadioThemes = async (lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = CACHE_KEYS.radioThemes(normalizedLang);
  
  return cachedFetch(
    cacheKey,
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/radio/themes?lang=${normalizedLang}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data.themes || data || [];
    },
    { ttl: CACHE_TTL.THEMES }
  );
};

/**
 * Get radio channels
 * @param {string} lang - Language code (fr/en)
 * @returns {Promise<Array>} - Array of channel objects
 */
export const getRadioChannels = async (lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = CACHE_KEYS.radioChannels(normalizedLang);
  
  return cachedFetch(
    cacheKey,
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/radio/channels?lang=${normalizedLang}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data.channels || data || [];
    },
    { ttl: CACHE_TTL.THEMES }
  );
};

/**
 * Get radio tracks for a channel
 * @param {string} channelId - Channel ID
 * @param {string} lang - Language code (fr/en)
 * @returns {Promise<Array>} - Array of track objects
 */
export const getRadioTracks = async (channelId, lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = CACHE_KEYS.radioTracks(channelId, normalizedLang);
  
  return cachedFetch(
    cacheKey,
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/radio/channels/${channelId}/playlist?lang=${normalizedLang}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data.sounds || data || [];
    },
    { ttl: CACHE_TTL.TRACKS }
  );
};

// =============================================================================
// SOUNDSCAPES CATALOG
// =============================================================================

/**
 * Get soundscapes themes (categories)
 * @param {string} lang - Language code (fr/en)
 * @returns {Promise<Array>} - Array of theme objects
 */
export const getSoundscapesThemes = async (lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = CACHE_KEYS.soundscapesThemes(normalizedLang);
  
  return cachedFetch(
    cacheKey,
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/therapy/themes?lang=${normalizedLang}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data.themes || data || [];
    },
    { ttl: CACHE_TTL.THEMES }
  );
};

/**
 * Get soundscapes tracks for a theme
 * @param {string} theme - Theme/category name
 * @param {string} lang - Language code (fr/en)
 * @returns {Promise<Array>} - Array of track objects
 */
export const getSoundscapesTracks = async (theme, lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = CACHE_KEYS.soundscapesTracks(theme, normalizedLang);
  
  return cachedFetch(
    cacheKey,
    async () => {
      // BUILD 65: Fixed endpoint - use /api/sounds/{theme}
      // Theme IDs map to R2 folder names (e.g., 'classical', 'sleep', 'nature')
      const response = await fetch(`${API_BASE_URL}/api/sounds/${theme}?lang=${normalizedLang}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return data.sounds || data || [];
    },
    { ttl: CACHE_TTL.TRACKS }
  );
};

// =============================================================================
// PRELOADING - Fire and forget
// =============================================================================

/**
 * Preload radio catalog (themes + channels + first theme's tracks)
 * Call this when user enters audio section
 * @param {string} lang - Language code
 */
export const preloadRadioCatalog = (lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const timer = perfTimer();
  
  console.log(`[Perf][Preload] Starting radio catalog preload (lang=${normalizedLang})`);
  
  // Preload themes
  preload(
    CACHE_KEYS.radioThemes(normalizedLang),
    () => fetch(`${API_BASE_URL}/api/radio/themes?lang=${normalizedLang}`).then(r => r.json()).then(d => d.themes || d || []),
    CACHE_TTL.THEMES
  );
  
  // Preload channels
  preload(
    CACHE_KEYS.radioChannels(normalizedLang),
    () => fetch(`${API_BASE_URL}/api/radio/channels?lang=${normalizedLang}`).then(r => r.json()).then(d => d.channels || d || []),
    CACHE_TTL.THEMES
  );
  
  // Preload last selected theme's tracks (or first theme if none)
  const lastTheme = getLastSelectedTheme('radio');
  if (lastTheme) {
    console.log(`[Perf][Preload] Preloading last radio theme: ${lastTheme}`);
    preload(
      CACHE_KEYS.radioTracks(lastTheme, normalizedLang),
      () => fetch(`${API_BASE_URL}/api/radio/channels/${lastTheme}/playlist?lang=${normalizedLang}`).then(r => r.json()).then(d => d.sounds || d || []),
      CACHE_TTL.TRACKS
    );
  }
  
  console.log(`[Perf][Preload] Radio catalog preload initiated in ${timer.elapsed()}ms`);
};

/**
 * Preload soundscapes catalog (themes + last selected theme's tracks)
 * Call this when user enters audio section
 * @param {string} lang - Language code
 */
export const preloadSoundscapesCatalog = (lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const timer = perfTimer();
  
  console.log(`[Perf][Preload] Starting soundscapes catalog preload (lang=${normalizedLang})`);
  
  // Preload themes
  preload(
    CACHE_KEYS.soundscapesThemes(normalizedLang),
    () => fetch(`${API_BASE_URL}/api/therapy/themes?lang=${normalizedLang}`).then(r => r.json()).then(d => d.themes || d || []),
    CACHE_TTL.THEMES
  );
  
  // Preload last selected theme's tracks
  const lastTheme = getLastSelectedTheme('soundscapes');
  if (lastTheme) {
    console.log(`[Perf][Preload] Preloading last soundscapes theme: ${lastTheme}`);
    preload(
      CACHE_KEYS.soundscapesTracks(lastTheme, normalizedLang),
      // V1.1.1 FIX: Use /api/library-sounds/{theme} - same as TherapyPlayer.js
      () => fetch(`${API_BASE_URL}/api/library-sounds/${lastTheme}?lang=${normalizedLang}`).then(r => r.json()),
      CACHE_TTL.TRACKS
    );
  }
  
  console.log(`[Perf][Preload] Soundscapes catalog preload initiated in ${timer.elapsed()}ms`);
};

/**
 * Preload all catalog data (call on app init or audio tab entry)
 * V1.1.1: Radio removed - only preload Soundscapes
 * @param {string} lang - Language code
 */
export const preloadAllCatalog = (lang = 'fr') => {
  // V1.1.1: preloadRadioCatalog disabled - Radio removed
  preloadSoundscapesCatalog(lang);
};

// =============================================================================
// THEME PERSISTENCE
// =============================================================================

/**
 * Save last selected radio theme
 * @param {string} themeId - Theme/channel ID
 */
export const saveRadioTheme = (themeId) => {
  saveLastSelectedTheme('radio', themeId);
};

/**
 * Save last selected soundscapes theme
 * @param {string} themeId - Theme/category ID
 */
export const saveSoundscapesTheme = (themeId) => {
  saveLastSelectedTheme('soundscapes', themeId);
};

/**
 * Get last selected radio theme
 * @returns {string|null}
 */
export const getLastRadioTheme = () => getLastSelectedTheme('radio');

/**
 * Get last selected soundscapes theme
 * @returns {string|null}
 */
export const getLastSoundscapesTheme = () => getLastSelectedTheme('soundscapes');

// =============================================================================
// CACHE STATUS - For UI to check if data is cached
// =============================================================================

/**
 * Check if radio themes are cached
 * @param {string} lang - Language code
 * @returns {boolean}
 */
export const isRadioThemesCached = (lang = 'fr') => {
  return getFromCache(CACHE_KEYS.radioThemes(normalizeLang(lang))) !== null;
};

/**
 * Check if soundscapes themes are cached
 * @param {string} lang - Language code
 * @returns {boolean}
 */
export const isSoundscapesThemesCached = (lang = 'fr') => {
  return getFromCache(CACHE_KEYS.soundscapesThemes(normalizeLang(lang))) !== null;
};

/**
 * Check if tracks for a theme are cached
 * @param {string} type - 'radio' or 'soundscapes'
 * @param {string} themeId - Theme ID
 * @param {string} lang - Language code
 * @returns {boolean}
 */
export const isTracksCached = (type, themeId, lang = 'fr') => {
  const normalizedLang = normalizeLang(lang);
  const cacheKey = type === 'radio' 
    ? CACHE_KEYS.radioTracks(themeId, normalizedLang)
    : CACHE_KEYS.soundscapesTracks(themeId, normalizedLang);
  return getFromCache(cacheKey) !== null;
};

export default {
  // Radio
  getRadioThemes,
  getRadioChannels,
  getRadioTracks,
  
  // Soundscapes
  getSoundscapesThemes,
  getSoundscapesTracks,
  
  // Preloading
  preloadRadioCatalog,
  preloadSoundscapesCatalog,
  preloadAllCatalog,
  
  // Theme persistence
  saveRadioTheme,
  saveSoundscapesTheme,
  getLastRadioTheme,
  getLastSoundscapesTheme,
  
  // Cache status
  isRadioThemesCached,
  isSoundscapesThemesCached,
  isTracksCached
};
